import PropTypes from 'prop-types'
import { useTheme } from '@emotion/react'
import {
  BlockGrid,
  Container,
  ListItem,
  Row,
  RowColumn,
  Section,
  SectionTitle,
} from 'chaoskit/src/components'
import { generateGradient } from 'chaoskit/src/assets/styles/utility/gradient'

import ButtonLink from '~components/ButtonLink'
import Data from '~components/Data'
import colorfulBackground from '~media/home/colorfulBackground.svg'
import { ReactComponent as HowItRunsImage } from '~media/product/overview/buildMoreReliable.svg'
import { ReactComponent as SupportedPlatformsImage } from '~media/product/overview/supportedPlatformsImage.svg'
import { ReactComponent as CycleSvg } from '~media/home/highlight.svg'
import { ReactComponent as ReliabilitySvg } from '~media/product/overview/cloud.svg'
import { ReactComponent as StopWatchSvg } from '~media/product/overview/latency-stopwatch.svg'
import { ReactComponent as TrackSvg } from '~media/product/overview/kvp-track.svg'
import { ReactComponent as ConnectSvg } from '~media/product/overview/connect.svg'
import { ReactComponent as SearchSvg } from '~media/product/overview/search.svg'
import { ReactComponent as CompassSvg } from '~media/product/overview/guided.svg'
import { ReactComponent as CheckboxSvg } from '~media/product/overview/checkbox.svg'
import { ReactComponent as ShieldSvg } from '~media/home/shield.svg'
import { ReactComponent as LeastPermissionsSvg } from '~media/product/overview/least-permissions.svg'
import { ReactComponent as SecureUserManagementSvg } from '~media/product/overview/secure-user-management.svg'
import { ReactComponent as AuditTrailsSvg } from '~media/product/overview/audit-trails.svg'
import { ReactComponent as PartyTestingSvg } from '~media/product/overview/3rd-party-testing.svg'
import { ReactComponent as RecreateSVG } from '~media/home/experiments.svg'
import { ReactComponent as HighlightSVG } from '~media/product/overview/uncoverAndMeasure.svg'
import { ReactComponent as BuildSVG } from '~media/product/overview/buildConfidence.svg'

const ProductIcon = ({ icon, ...rest }) => {
  let Component

  switch (icon) {
    case 'cycle':
      Component = CycleSvg
      break
    case 'reliability':
      Component = ReliabilitySvg
      break
    case 'stopWatch':
      Component = StopWatchSvg
      break
    case 'graph':
      Component = TrackSvg
      break
    case 'connect':
      Component = ConnectSvg
      break
    case 'search':
      Component = SearchSvg
      break
    case 'compass':
      Component = CompassSvg
      break
    case 'checkbox':
      Component = CheckboxSvg
      break
    case 'shield':
      Component = ShieldSvg
      break
    case 'secureUserManagement':
      Component = SecureUserManagementSvg
      break
    case 'auditTrails':
      Component = AuditTrailsSvg
      break
    case 'leastPermissions':
      Component = LeastPermissionsSvg
      break
    case 'partyTesting':
      Component = PartyTestingSvg
      break
    case 'recreate':
      Component = RecreateSVG
      break
    case 'highlight':
      Component = HighlightSVG
      break
    case 'build':
      Component = BuildSVG
      break
    default:
      Component = () => null
  }

  return <Component {...rest} />
}

ProductIcon.propTypes = {
  icon: PropTypes.oneOf([
    'cycle',
    'reliability',
    'stopWatch',
    'graph',
    'connect',
    'search',
    'compass',
    'checkbox',
    'shield',
    'secureUserManagement',
    'auditTrails',
    'leastPermissions',
    'partyTesting',
    'recreate',
    'highlight',
    'build',
  ]),
}

export const FirstSection = ({ data }) => {
  const theme = useTheme()

  return (
    <Section
      css={{
        background: generateGradient({
          start: theme.brand.violet,
          stop: theme.color.info.base,
          position: 'to right',
        }),
        [theme.mq.medium]: {
          paddingTop: '120px',
          paddingBottom: '120px',
        },
      }}
    >
      <Container size="large" className="u-contrast">
        <SectionTitle
          css={{
            '& .CK__SectionTitle__Header': {
              marginBottom: '40px !important',
            },
            '& .CK__SectionTitle__Sub': {
              fontSize: '24px',
              maxWidth: 'none',
              '& p': {
                marginBottom: '36px',
              },
            },
          }}
          as="h2"
          title={<Data>{data.heading}</Data>}
          sub={<Data>{data.blurb}</Data>}
        />
      </Container>
    </Section>
  )
}

FirstSection.propTypes = {
  data: PropTypes.shape({
    heading: PropTypes.string.isRequired,
    blurb: PropTypes.string.isRequired,
  }),
}

export const SecondSection = ({ data }) => {
  const theme = useTheme()

  return (
    <div>
      {data.map((item, index) => (
        <Section
          key={item.heading}
          css={{
            background: index % 2 !== 0 ? `url(${colorfulBackground})` : 'none',
            backgroundPosition: 'center',
            backgroundRepeat: 'no-repeat',
            backgroundSize: 'cover',
            [theme.mq.medium]: {
              paddingTop: '120px',
              paddingBottom: '120px',
            },
          }}
        >
          <Container size="large">
            <div
              css={{
                display: 'grid',
                gap: theme.space.large,
                marginBottom:
                  index === data.length - 1 ? null : theme.space.xlarge,

                [theme.mq.medium]: {
                  alignItems: 'center',
                  gridTemplateColumns: 'repeat(12, minmax(0, 1fr))',
                  gap: theme.space.xlarge,
                  marginBottom:
                    index === data.length - 1 ? null : theme.space['2xlarge'],
                },
              }}
            >
              <div
                css={{
                  [theme.mq.medium]: {
                    gridColumn: index % 2 === 0 ? '1 / span 6' : '7 / span 6',
                    gridRow: 1,
                  },
                }}
              >
                <h3
                  css={{
                    marginBottom: '40px',
                    [theme.mq.medium]: { fontSize: '55px' },
                  }}
                >
                  <Data>{item.heading}</Data>
                </h3>
                <div css={{ fontSize: '24px' }}>
                  <div css={{ marginBottom: theme.space.medium }}>
                    <Data>{item.subHeading}</Data>
                  </div>
                  <div>
                    {item.pointers && (
                      <ul>
                        {item.pointers.map((pointer) => (
                          <li key={pointer.pointer}>
                            <Data>{pointer.pointer}</Data>
                          </li>
                        ))}
                      </ul>
                    )}
                  </div>
                </div>
              </div>
              <div
                css={{
                  [theme.mq.medium]: {
                    gridColumn: index % 2 === 0 ? '7 / span 6' : '1 / span 6',
                    gridRow: 1,
                    direction: index % 2 === 0 ? null : 'rtl',
                    order: index % 2 === 0 ? 1 : null,
                  },
                }}
              >
                <ProductIcon icon={item.animationPointer} />
              </div>
            </div>
          </Container>
        </Section>
      ))}
    </div>
  )
}

SecondSection.propTypes = {
  data: PropTypes.array,
}

export const ThirdSection = ({ data }) => {
  const theme = useTheme()

  return (
    <Section
      css={{
        background:
          'radial-gradient(46.77% 147.55% at 69.9% 62.72%, #311173 0%, #1F1638 100%)',
        [theme.mq.medium]: {
          paddingTop: '120px',
          paddingBottom: '120px',
        },
      }}
    >
      <Container size="large" className="u-contrast">
        <Row gutter={{ base: 'large' }}>
          <RowColumn
            size={{ medium: 8 }}
            css={{
              [theme.mq.medium]: {
                alignSelf: 'center',
              },
            }}
          >
            <SectionTitle
              as="h3"
              title={<Data>{data.heading}</Data>}
              css={{
                justifyItems: 'left',
                textAlign: 'left',
                '& .CK__SectionTitle__Header': {
                  fontSize: '59px',
                },
              }}
            />
          </RowColumn>
          <RowColumn
            size={{ medium: 4 }}
            css={{
              [theme.mq.small__max]: {
                textAlign: 'center',
              },
            }}
          >
            <ButtonLink
              noContrast
              type="default"
              url={data.buttonURL}
              css={{
                [theme.mq.small__max]: {
                  width: '100%',
                },
              }}
            >
              {data.buttonText}
            </ButtonLink>
            {data.extraText && (
              <div
                css={{
                  marginTop: '29px',
                  fontWeight: '600',
                  fontSize: '20px',
                  lineHeight: '1.1',
                  p: {
                    marginBottom: 0,
                    marginTop: 0,
                  },
                }}
              >
                <Data>{data.extraText}</Data>
              </div>
            )}
          </RowColumn>
        </Row>
      </Container>
    </Section>
  )
}

ThirdSection.propTypes = {
  data: PropTypes.shape({
    heading: PropTypes.string.isRequired,
    buttonText: PropTypes.string.isRequired,
    buttonURL: PropTypes.string.isRequired,
    extraText: PropTypes.string.isRequired,
  }),
}

export const FourthSection = ({ data }) => {
  const theme = useTheme()

  return (
    <Section
      css={{
        background: theme.brand.stone100,
        [theme.mq.medium]: {
          paddingTop: '120px',
          paddingBottom: '120px',
        },
      }}
    >
      <Container size="large">
        <Row
          gutter={{ base: 'large' }}
          css={{
            [theme.mq.medium]: {
              alignItems: 'center',
            },
          }}
        >
          <RowColumn size={{ medium: 6 }}>
            <div
              css={{
                [theme.mq.medium]: {
                  width: '90%',
                },
              }}
            >
              <div
                css={{
                  fontSize: '25px',
                  lineHeight: '1.1',
                  fontWeight: theme.fontWeight.bold,
                  marginBottom: theme.space.medium,
                }}
              >
                <Data>{data.heading}</Data>
              </div>
              <SectionTitle
                as="h2"
                title={<Data>{data.subHeading}</Data>}
                css={{
                  textAlign: 'left',
                  marginBottom: '40px !important',
                }}
              />
              <div css={{ marginBottom: '40px' }}>
                <Data>{data.blurb}</Data>
              </div>
              <ButtonLink
                noContrast
                type="primary"
                url={data.buttonURL}
                css={{
                  [theme.mq.small__max]: {
                    width: '100%',
                  },
                }}
              >
                {data.buttonText}
              </ButtonLink>
            </div>
          </RowColumn>
          <RowColumn size={{ medium: 6 }}>
            <HowItRunsImage />
          </RowColumn>
        </Row>
      </Container>
    </Section>
  )
}

FourthSection.propTypes = {
  data: PropTypes.shape({
    heading: PropTypes.string.isRequired,
    subHeading: PropTypes.string.isRequired,
    blurb: PropTypes.string.isRequired,
    buttonURL: PropTypes.string.isRequired,
    buttonText: PropTypes.string.isRequired,
  }),
}

export const FifthSection = ({ data }) => {
  const theme = useTheme()

  return (
    <Section
      css={{
        [theme.mq.medium]: {
          paddingTop: '120px',
          paddingBottom: '120px',
        },
      }}
    >
      <Container size="large">
        <div
          css={{
            fontSize: '25px',
            lineHeight: '1.1',
            fontWeight: theme.fontWeight.bold,
            marginBottom: theme.space.medium,

            [theme.mq.medium]: {
              textAlign: 'center',
            },
          }}
        >
          <Data>{data.heading}</Data>
        </div>
        <SectionTitle
          as="h2"
          title={<Data>{data.subHeading}</Data>}
          css={{
            marginBottom: '96px !important',
            [theme.mq.medium]: {
              textAlign: 'center',
              '& .CK__SectionTitle__Header': {
                width: '80%',
              },
            },
            [theme.mq.small__max]: { textAlign: 'left' },
          }}
        />
        <Row
          gutter={{ base: 'large' }}
          css={{
            marginLeft: '-36px',
            marginTop: '-64px',
          }}
        >
          {data.features.map((highlight) => (
            <RowColumn
              size={{ medium: 4 }}
              css={{
                margin: '0 auto',
                paddingLeft: '36px !important',
                paddingTop: '64px !important',
              }}
              key={highlight.icon}
            >
              <div>
                <ProductIcon
                  css={{
                    height: 70,
                    display: 'block',
                    margin: 'auto',
                    marginBottom: theme.space.medium,
                  }}
                  icon={highlight.icon}
                />
                <div css={{ textAlign: 'center' }}>
                  <h4 css={{ marginBottom: theme.space.base }}>
                    {highlight.heading}
                  </h4>
                  <div
                    css={{
                      fontSize: '16px',
                      lineHeight: '1.6',
                    }}
                  >
                    <Data>{highlight.blurb}</Data>
                  </div>
                </div>
              </div>
            </RowColumn>
          ))}
        </Row>
      </Container>
    </Section>
  )
}

FifthSection.propTypes = {
  data: PropTypes.shape({
    heading: PropTypes.string.isRequired,
    subHeading: PropTypes.string.isRequired,
    features: PropTypes.array,
  }),
}

export const SixthSection = ({ data }) => {
  const theme = useTheme()

  return (
    <Section
      css={{
        background: `url(${colorfulBackground})`,
        backgroundPosition: 'center',
        backgroundRepeat: 'no-repeat',
        backgroundSize: 'cover',
        [theme.mq.medium]: {
          paddingTop: '120px',
          paddingBottom: '120px',
        },
      }}
    >
      <Container size="large">
        <Row
          gutter={{ base: 'xlarge' }}
          css={{
            [theme.mq.medium]: { alignItems: 'center' },
          }}
        >
          <RowColumn
            size={{ medium: 6 }}
            css={{
              [theme.mq.medium]: { maxWidth: '590px' },
            }}
          >
            <div
              css={{
                fontSize: '25px',
                lineHeight: '1.1',
                fontWeight: theme.fontWeight.bold,
                marginBottom: theme.space.medium,
              }}
            >
              <Data>{data.heading}</Data>
            </div>
            <SectionTitle
              as="h2"
              css={{
                textAlign: 'left',
              }}
              title={<Data>{data.subHeading}</Data>}
            />
          </RowColumn>
          <RowColumn size={{ medium: 6 }}>
            <BlockGrid
              size={{ medium: 1 }}
              gutter={{ base: 'large' }}
              css={{
                marginTop: '-48px',
                marginLeft: '-48px',
              }}
            >
              {data.cases.map((useCase) => (
                <ListItem
                  key={useCase.icon}
                  css={{
                    paddingLeft: '48px !important',
                    paddingTop: '48px !important',
                  }}
                >
                  <div
                    css={{
                      display: 'grid',
                      gap: theme.space.large,
                      alignItems: 'center',
                      textAlign: 'center',

                      [theme.mq.medium]: {
                        gridTemplateColumns: `65px 1fr`,
                        alignItems: 'start',
                        textAlign: 'left',
                      },
                    }}
                  >
                    <ProductIcon
                      css={{
                        height: 65,
                        display: 'block',
                        margin: 'auto',
                      }}
                      icon={useCase.icon}
                    />
                    <div
                      css={{
                        ...theme.text.large,
                        alignSelf: 'center',
                      }}
                    >
                      {useCase.heading}
                    </div>
                  </div>
                </ListItem>
              ))}
            </BlockGrid>
          </RowColumn>
        </Row>
      </Container>
    </Section>
  )
}

SixthSection.propTypes = {
  data: PropTypes.shape({
    heading: PropTypes.string.isRequired,
    subHeading: PropTypes.string.isRequired,
    cases: PropTypes.array,
  }),
}

export const SeventhSection = ({ data }) => {
  const theme = useTheme()

  return (
    <Section
      css={{
        background: theme.brand.stone100,
        [theme.mq.medium]: {
          paddingTop: '120px',
          paddingBottom: '120px',
        },
      }}
    >
      <Container size="large">
        <Row
          gutter={{ base: 'large' }}
          css={{
            direction: 'rtl',
            [theme.mq.medium]: {
              alignItems: 'center',
            },
          }}
        >
          <RowColumn size={{ medium: 6 }} css={{ textAlign: 'left' }}>
            <div
              css={{
                fontSize: '25px',
                lineHeight: '1.1',
                fontWeight: theme.fontWeight.bold,
                marginBottom: theme.space.medium,
              }}
            >
              <Data>{data.heading}</Data>
            </div>
            <SectionTitle
              as="h2"
              title={<Data>{data.subHeading}</Data>}
              css={{
                textAlign: 'left',
                marginBottom: '40px !important',
              }}
            />
            <div
              css={{
                direction: 'ltr',
              }}
            >
              <Data>{data.blurb}</Data>
            </div>
          </RowColumn>
          <RowColumn
            size={{ medium: 6 }}
            css={{
              [theme.mq.medium]: {
                direction: 'ltr',
              },
            }}
          >
            <SupportedPlatformsImage
              css={{
                [theme.mq.medium]: {
                  width: '80%',
                },
              }}
            />
          </RowColumn>
        </Row>
      </Container>
    </Section>
  )
}

SeventhSection.propTypes = {
  data: PropTypes.shape({
    heading: PropTypes.string.isRequired,
    subHeading: PropTypes.string.isRequired,
    blurb: PropTypes.string.isRequired,
  }),
}

export const EighthSection = ({ data }) => {
  const theme = useTheme()

  return (
    <Section
      css={{
        [theme.mq.medium]: {
          paddingTop: '120px',
          paddingBottom: '120px',
        },
      }}
    >
      <Container size="large">
        <SectionTitle
          as="h2"
          title={<Data>{data.heading}</Data>}
          sub={<Data>{data.blurb}</Data>}
          css={{
            marginBottom: '96px !important',
            '.CK__SectionTitle__Header': {
              marginBottom: '40px',
            },
            '.CK__SectionTitle__Sub': {
              marginBottom: '40px',
            },
            [theme.mq.small__max]: {
              textAlign: 'left',
            },
          }}
        >
          <ButtonLink
            type="primary"
            url="/security"
            css={{
              [theme.mq.small__max]: {
                width: '100%',
              },
            }}
          >
            {data.buttonText}
          </ButtonLink>
        </SectionTitle>
        <Row gutter={{ base: 'xlarge' }}>
          {data.items.map((securityItem) => (
            <RowColumn
              size={{ medium: 6 }}
              css={{ margin: '0 auto' }}
              key={securityItem.heading}
            >
              <div
                css={{
                  display: 'grid',
                  gap: theme.space.large,
                  alignItems: 'center',
                  textAlign: 'center',

                  [theme.mq.medium]: {
                    gridTemplateColumns: `65px 1fr`,
                    alignItems: 'start',
                    textAlign: 'left',
                  },
                }}
              >
                <div>
                  <ProductIcon
                    css={{
                      height: 65,
                      margin: 'auto',

                      [theme.mq.medium]: { margin: 0 },
                    }}
                    icon={securityItem.icon}
                  />
                </div>
                <div css={{ paddingTop: theme.space.small }}>
                  <div
                    css={{
                      fontSize: '33px',
                      lineHeight: '1.1',
                      fontWeight: theme.fontWeight.bold,
                      marginBottom: '12px',
                    }}
                  >
                    <Data>{securityItem.heading}</Data>
                  </div>
                  <div
                    css={{
                      fontSize: '16px',
                      lineHeight: theme.lineHeight.large,
                    }}
                  >
                    <Data>{securityItem.blurb}</Data>
                  </div>
                </div>
              </div>
            </RowColumn>
          ))}
        </Row>
      </Container>
    </Section>
  )
}

EighthSection.propTypes = {
  data: PropTypes.shape({
    heading: PropTypes.string.isRequired,
    blurb: PropTypes.string.isRequired,
    buttonText: PropTypes.string.isRequired,
    items: PropTypes.array,
  }),
}
